/**
 * The open-tickets tray (v0.9.0's P5; ADR 0188; the owner-approved mockup's
 * `trayHTML` and `trayRowHTML`): a tech's open tickets from every day, in a
 * drawer on the right. Logged isn't the goal, closed is.
 *
 * - **What it lists:** `GET /api/v1/tickets`, the tech's (or, for an owner,
 *   the viewed tech's) tickets not closed, from every day: logged through Axon
 *   Time, and those made in Autotask that are assigned to them or have their
 *   time (ADR 0183). Oldest first.
 * - **Where:** a drawer on the window's right edge, with a tab that's always
 *   there (the owner, 2026-10-21, after rc.1; ADR 0190). On a wide screen it
 *   reaches from the board's Closed column to the window's edge, so Needs
 *   you, Ready to log and Logged - Open stay in view as drop targets. The tab
 *   opens and closes it, and so do × and Escape. A block's popup sits above
 *   it.
 * - **Each row:** the number (its block's popup when it has one on this day),
 *   the client (never a HIPAA client's title), its age, its state and
 *   Autotask status, the time logged, and its last logging (a link to that
 *   day) or its making; one made in Autotask says so, and why it's the
 *   tech's.
 * - **Closing:** each open row's **Close** asks first (the owner's decision
 *   5); **Close selected (N)** closes the ticked ones, after a confirm that
 *   lists them (`POST /api/v1/tickets/close`). A row says Closing…, Couldn't
 *   close with **Try again**, or Closed, and a closed row leaves after about
 *   3.5 seconds.
 * - **A drop target:** each row takes a block dragged from the board, by
 *   P4's rules (ADR 0187), so today's work can go onto an older ticket.
 * - **Tickets you're secondary on** (v0.9.0's P10; ADR 0192): a lower
 *   section, below the tech's own, of the open tickets they're only a
 *   secondary resource on in Autotask, each with its primary's name. A drop
 *   target too, but no checkbox and no Close: they may log to one, not close
 *   it. Logged to, it's theirs, and moves up on the next read. Neither the
 *   tab, the tile nor the header counts them. With none, it isn't shown.
 *
 * The server decides who may close what, and as whom it's written. Nothing
 * here writes to Autotask by itself.
 */
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ApiError } from "../../api/client";
import type { TicketAction, TicketState, TrayTicket } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Badge } from "../../components/ui/Badge";
import { TrayIcon } from "../../components/ui/Icons";
import { IconButton } from "../../components/ui/IconButton";
import { formatShortDay, todayIn } from "../../lib/dates";
import { formatDuration } from "../../lib/durations";
import { isOpenList } from "../../lib/keys";
import { useAnnounce } from "./announce";
import { DropNote } from "./BlockCard";
import type { BlockView, LoggedEntryView, TicketTarget } from "./model";
import { ACTION_POLL_MS, isUnfinished, ticketActionQuery } from "./ticketActions";
import { STATE_CHIPS, ageOf, canClose, closedByText } from "./tickets";
import { DRAWER_PX, drawerWidth, oldestOf, ownTickets, secondaryTickets, useTrayTickets } from "./trayTickets";
import type { DropZone } from "./useDragToMerge";

/** How long a row just closed stays, saying so, before it leaves (the mockup's). */
export const CLOSED_ROW_MS = 3_500;

/** Retry what may pass on a second try; a refusal (4xx) won't. */
function retry(failures: number, error: Error): boolean {
  return !(error instanceof ApiError && error.status < 500) && failures < 2;
}


function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** A refused close, in the API's words. */
function refusalOf(error: Error): string {
  return error instanceof ApiError && error.detail ? error.detail : error.message;
}

/** As techs know it; its ID until its number is known. */
function nameOf(ticket: TrayTicket): string {
  return ticket.ticket_number ?? String(ticket.autotask_ticket_id);
}

/** Oldest first, as the API sorts them: by when its age counts from, then by ticket. */
function byAge(a: TrayTicket, b: TrayTicket): number {
  const at = (t: TrayTicket) => (t.age_from === null ? Number.POSITIVE_INFINITY : Date.parse(t.age_from));
  return at(a) - at(b) || a.autotask_ticket_id - b.autotask_ticket_id;
}

/** What a row's chip says beside it (the mockup's `trayRowHTML`). */
function stateWords(ticket: TrayTicket, state: TicketState): { text: string | null; warn: boolean } {
  switch (state) {
    case "open":
      return { text: ticket.status_label, warn: false };
    case "closing":
      return { text: "Setting it to Complete", warn: false };
    case "reopening":
      // A reopen to log to it (P9; ADR 0197): seconds, then it's open.
      return { text: "Setting it to In Progress", warn: false };
    case "close_failed":
      return { text: "Try again", warn: true };
    case "closed":
      return {
        text: closedByText({ closedBy: ticket.closed_by ?? "axon_time", closedByName: null }),
        warn: false,
      };
    case "unknown":
      return { text: null, warn: false };
  }
}

/** Why it's theirs, for one made in Autotask: "Assigned to you", "You logged time". */
function whyWords(reasons: TrayTicket["reasons"], who: string | null): string[] {
  const words: string[] = [];
  if (reasons.includes("assigned")) {
    words.push(who === null ? "Assigned to you" : `Assigned to ${who}`);
  }
  if (reasons.includes("logged_autotask")) {
    words.push(who === null ? "You logged time" : `${who} logged time`);
  }
  return words;
}

/** From this wide, the board's columns share a row, and the drawer covers Closed. */
const WIDE_PX = 1100;
/** Where the drawer sits: its top, and on a wide screen its width (null: the stylesheet's). */
type DrawerPlace = { top: number; width: number | null };

/**
 * Where the drawer sits (ADR 0190), as the window changes and the page
 * scrolls. Its top is just under the header while the header's in view (12
 * px, the page's gap), so the date and the user switcher stay in reach, and
 * the window's top once it's gone. From 1100 px wide, where the columns share
 * a row, its width reaches from the board's Closed column
 * (`[data-column="closed"]`) to the window's right edge.
 */
function useDrawerPlace(): DrawerPlace {
  const [place, setPlace] = useState<DrawerPlace>({ top: 0, width: null });
  useLayoutEffect(() => {
    const measure = () => {
      const header = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
      const top = Math.max(0, Math.round(header + 12));
      let width: number | null = null;
      if (window.innerWidth >= WIDE_PX) {
        const column = document.querySelector('[data-column="closed"]');
        width =
          column === null
            ? DRAWER_PX
            : drawerWidth(document.documentElement.clientWidth - column.getBoundingClientRect().left);
      }
      setPlace((last) => (last.top === top && last.width === width ? last : { top, width }));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, []);
  return place;
}

/** The transition the drawer slides with, in seconds: none under reduced motion (or in a test). */
function slideSeconds(element: Element | null): number {
  if (element === null) {
    return 0;
  }
  const seconds = Number.parseFloat(getComputedStyle(element).transitionDuration);
  return Number.isNaN(seconds) ? 0 : seconds;
}

export interface OpenTicketsDrawerProps extends Omit<OpenTicketsTrayProps, "id" | "leaving"> {
  /** The tray is open (the address says `tickets=open`). */
  open: boolean;
  /** The tab, while it's closed. */
  onOpen: () => void;
}

/**
 * The open-tickets drawer (ADR 0190; the owner, 2026-10-21: "id like the
 * tray to be permeant fixed to the right side of the screen, and if you
 * click on the little pixels that stick out it opens"): flush with the
 * window's right edge, from under the header to the window's foot, with a
 * tab that's always there. Closed, only the tab shows, at the window's edge;
 * open, it rides on the drawer's left edge. Not modal: the board stays in use
 * beside it. It slides in and out, only where motion is wanted.
 */
export function OpenTicketsDrawer({ open, onOpen, onClose, userId, ...tray }: OpenTicketsDrawerProps) {
  const trayId = useId();
  const drawer = useRef<HTMLDivElement>(null);
  const tab = useRef<HTMLButtonElement>(null);
  const place = useDrawerPlace();
  const tickets = useTrayTickets(userId);
  // The tech's own: not those they're only a secondary resource on (ADR 0192).
  const count = tickets.data === undefined ? undefined : ownTickets(tickets.data.tickets).length;
  // Just closed: the tray stays while it slides out, out of reach. Each
  // opening is a tray of its own, as it was before the drawer, even one
  // reopened as it slides out.
  const [leaving, setLeaving] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  const [openings, setOpenings] = useState(0);
  if (wasOpen !== open) {
    setWasOpen(open);
    setLeaving(!open);
    if (open) {
      setOpenings((n) => n + 1);
    }
  }
  useEffect(() => {
    if (!leaving) {
      return;
    }
    const seconds = slideSeconds(drawer.current);
    if (seconds === 0) {
      setLeaving(false);
      return;
    }
    // In case its transitionend never comes.
    const timer = setTimeout(() => {
      setLeaving(false);
    }, seconds * 1000 + 100);
    return () => {
      clearTimeout(timer);
    };
  }, [leaving]);

  const close = () => {
    onClose();
    // Focus goes back to what opened it (DayPage). Opened from the address,
    // focus left in the tray, or on nothing, goes to the tab.
    const focused = document.activeElement;
    if (focused === null || focused === document.body || (focused !== tab.current && drawer.current?.contains(focused))) {
      tab.current?.focus();
    }
  };

  return (
    <div
      ref={drawer}
      data-part="open-tickets-drawer"
      style={{
        top: `${String(place.top)}px`,
        width: place.width === null ? undefined : `${String(place.width)}px`,
      }}
      onTransitionEnd={(event) => {
        if (event.target === drawer.current) {
          setLeaving(false);
        }
      }}
      className={
        "fixed right-0 bottom-0 z-20 w-full ease-out motion-safe:transition-transform motion-safe:duration-200 " +
        "min-[560px]:w-[440px] " +
        (open ? "translate-x-0" : "translate-x-full")
      }
    >
      {/* The tab: at the window's edge while it's closed, on the drawer's
          left edge while it's open (inside it on a phone, where the drawer is
          the window's width), about a third of the way down the window. */}
      <button
        ref={tab}
        type="button"
        data-part="open-tickets-tab"
        aria-expanded={open}
        aria-controls={trayId}
        title={open ? "Close the open tickets" : "Show your open tickets"}
        onClick={open ? close : onOpen}
        style={{ top: `max(16px, calc(33vh - ${String(place.top)}px))` }}
        className={
          "absolute right-full flex w-[26px] cursor-pointer flex-col items-center gap-2 rounded-l-[10px] border " +
          "border-r-0 border-line bg-panel py-2.5 text-text shadow-[-6px_0_16px_rgba(0,0,0,0.08)] hover:bg-panel-2 " +
          (open
            ? "max-[559px]:right-auto max-[559px]:left-0 max-[559px]:rounded-l-none max-[559px]:rounded-r-[10px] " +
              "max-[559px]:border-r max-[559px]:border-l-0 max-[559px]:shadow-none"
            : "")
        }
      >
        <span
          className={
            "inline-flex h-5 min-w-5 items-center justify-center rounded-pill px-1 font-mono text-[11px] font-semibold " +
            (count !== undefined && count > 0
              ? "bg-status-needs-client-bg text-status-needs-client-fg"
              : "border border-line text-muted")
          }
        >
          {count === undefined ? <span aria-hidden="true">–</span> : count}
        </span>
        {/* Its name: "3 Open tickets". On a phone, only the count and the
            icon show; the words are still its name. */}{" "}
        <span className="rotate-180 text-[11.5px] font-[650] tracking-[0.08em] whitespace-nowrap text-muted uppercase [writing-mode:vertical-rl] max-[559px]:sr-only">
          Open tickets
        </span>
        <span className="text-muted min-[560px]:hidden">
          <TrayIcon />
        </span>
      </button>
      {open || leaving ? (
        <OpenTicketsTray
          key={openings}
          {...tray}
          id={trayId}
          userId={userId}
          onClose={open ? close : () => undefined}
          leaving={!open}
        />
      ) : null}
    </div>
  );
}

export interface OpenTicketsTrayProps {
  /** Its element's ID: the tab's `aria-controls`. */
  id: string;
  /** Closed, and sliding out: out of reach, and hidden from a screen reader. */
  leaving?: boolean;
  /** Whose tickets: the viewed tech's; null for the signed-in user's own. */
  userId: string | null;
  /** Their name, when they're someone else: "Assigned to Casey". */
  otherName: string | null;
  /** The day the board shows, and the tech's own time zone and clock. */
  day: string;
  timeZone: string;
  now: Date;
  /** The board's blocks and dashed cards: where a ticket's number leads. */
  blocks: readonly BlockView[];
  entries: readonly LoggedEntryView[];
  /** Opens a block's popup, above the tray. */
  onOpenBlock: (blockId: string) => void;
  /** Each row as a drop target (P4's rules). */
  dropOn: (key: string, ticket: TicketTarget) => DropZone;
  /** × or Escape. */
  onClose: () => void;
  /**
   * Opened just now by the tech (the tile, the tab): focus goes to its
   * heading. Not when it's open already as a day loads (`?tickets=open`).
   */
  autoFocus?: boolean;
}

export function OpenTicketsTray({
  id,
  leaving = false,
  userId,
  otherName,
  day,
  timeZone,
  now,
  blocks,
  entries,
  onOpenBlock,
  dropOn,
  onClose,
  autoFocus = false,
}: OpenTicketsTrayProps) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const announce = useAnnounce();
  const headingId = useId();
  const secondaryId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const tray = useTrayTickets(userId);
  // The tech's own, above; those they're only a secondary resource on, in the
  // lower section (ADR 0192): never closed from here.
  const rows = tray.data === undefined ? undefined : ownTickets(tray.data.tickets);
  const secondary = tray.data === undefined ? [] : [...secondaryTickets(tray.data.tickets)].sort(byAge);
  // The closes asked for here (or tried again), each followed until it ends,
  // with its row as it was: a closed row stays, saying so, for a moment
  // after the tickets are read again without it.
  const [closes, setCloses] = useState<ReadonlyMap<number, { actionId: string; row: TrayTicket | undefined }>>(
    () => new Map(),
  );
  // A close refused, in the API's words, by ticket.
  const [refused, setRefused] = useState<ReadonlyMap<number, string>>(() => new Map());
  const [checked, setChecked] = useState<ReadonlySet<number>>(() => new Set());
  const [asking, setAsking] = useState<number | null>(null);
  const [bulkAsking, setBulkAsking] = useState(false);
  const [notice, setNotice] = useState<{ text: string; day: string | null } | null>(null);

  // Focus goes into the tray as it opens (its heading), and Escape closes it,
  // unless a popup (a block's, Owner tools) is open over it: that closes
  // first. The latest onClose, whatever its identity.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  const focusFirst = useRef(autoFocus);
  useEffect(() => {
    if (focusFirst.current) {
      heading.current?.focus();
    }
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key !== "Escape" ||
        event.defaultPrevented ||
        isOpenList(event.target) ||
        document.querySelector('[role="dialog"][aria-modal="true"]') !== null
      ) {
        return;
      }
      closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    void queryClient.invalidateQueries({ queryKey: ["day"] });
  };
  const follow = (made: readonly TicketAction[]) => {
    for (const action of made) {
      queryClient.setQueryData(ticketActionQuery(action.id), action);
    }
    setCloses((last) => {
      const next = new Map(last);
      for (const action of made) {
        const id = action.autotask_ticket_id;
        next.set(id, { actionId: action.id, row: rows?.find((t) => t.autotask_ticket_id === id) ?? last.get(id)?.row });
      }
      return next;
    });
    setRefused((last) => new Map([...last].filter(([id]) => !made.some((a) => a.autotask_ticket_id === id))));
    refresh();
  };
  const refuse = (id: number, words: string) => {
    setRefused((last) => new Map(last).set(id, words));
  };

  const closeOne = useMutation({
    // A new key each time it's confirmed: the same confirm sent twice is one close.
    mutationFn: ({ id, key }: { id: number; key: string }) => api.closeTicket(id, key),
    onSuccess: (action) => {
      follow([action]);
    },
    onError: (error, { id }) => {
      refuse(id, refusalOf(error));
      refresh();
    },
  });
  const tryAgain = useMutation({
    mutationFn: ({ actionId }: { id: number; actionId: string }) => api.retryTicketAction(actionId),
    onSuccess: (action) => {
      follow([action]);
    },
    onError: (error, { id }) => {
      refuse(id, refusalOf(error));
      refresh();
    },
  });
  const closeMany = useMutation({
    mutationFn: ({ ids, key }: { ids: number[]; key: string }) => api.closeTickets(ids, key),
    onSuccess: (result) => {
      follow(result.results.flatMap((r) => (r.accepted && r.action !== null ? [r.action] : [])));
      for (const r of result.results) {
        if (!r.accepted) {
          refuse(r.autotask_ticket_id, r.refusal ?? "It can't be closed now.");
        }
      }
      const accepted = result.results.filter((r) => r.accepted).length;
      const left = result.results.length - accepted;
      announce(
        left === 0
          ? `Closing ${count(accepted, "ticket", "tickets")}.`
          : `Closing ${count(accepted, "ticket", "tickets")}; ${count(left, "wasn't", "weren't")} closed.`,
      );
    },
    onError: (error, { ids }) => {
      for (const id of ids) {
        refuse(id, refusalOf(error));
      }
    },
  });

  // Each close asked for here, followed until it ends.
  const followed = useQueries({
    queries: [...closes.values()].map(({ actionId }) => ({
      queryKey: ticketActionQuery(actionId),
      queryFn: () => api.getTicketAction(actionId),
      refetchInterval: (query: { state: { data: TicketAction | undefined } }) =>
        isUnfinished(query.state.data) ? ACTION_POLL_MS : false,
      retry,
    })),
  });
  const actions = followed.map((q) => q.data).filter((a): a is TicketAction => a !== undefined);
  const actionOf = (ticketId: number): TicketAction | undefined => {
    const close = closes.get(ticketId);
    return close === undefined ? undefined : actions.find((a) => a.id === close.actionId);
  };
  // As each ends: the tickets and the day read again, and it's said. Each end
  // once (a close tried again ends again).
  const said = useRef(new Set<string>());
  useEffect(() => {
    const ended = actions.filter((a) => !isUnfinished(a) && !said.current.has(`${a.id}:${a.state}:${a.finished_at ?? ""}`));
    if (ended.length === 0) {
      return;
    }
    for (const a of ended) {
      said.current.add(`${a.id}:${a.state}:${a.finished_at ?? ""}`);
    }
    const name = (a: TicketAction) => a.ticket_number ?? String(a.autotask_ticket_id);
    const done = ended.filter((a) => a.state === "done");
    const failed = ended.filter((a) => a.state === "failed");
    const words = [
      done.length === 0 ? null : done.length === 1 && done[0] ? `Closed ticket ${name(done[0])}.` : `Closed ${count(done.length, "ticket", "tickets")}.`,
      failed.length === 0
        ? null
        : failed.length === 1 && failed[0]
          ? `Couldn't close ticket ${name(failed[0])}.`
          : `Couldn't close ${count(failed.length, "ticket", "tickets")}.`,
    ].filter((w): w is string => w !== null);
    announce(words.join(" "));
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    void queryClient.invalidateQueries({ queryKey: ["day"] });
  }, [actions, announce, queryClient]);
  // A closed row leaves after a moment: its close is forgotten here.
  const closedKey = actions
    .filter((a) => a.state === "done")
    .map((a) => a.id)
    .join(" ");
  useEffect(() => {
    if (closedKey === "") {
      return;
    }
    const gone = closedKey.split(" ");
    const timer = setTimeout(() => {
      setCloses((last) => new Map([...last].filter(([, close]) => !gone.includes(close.actionId))));
    }, CLOSED_ROW_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [closedKey]);

  /** As the tray sees it now: closing as soon as it's asked for, closed while it leaves. */
  const stateOf = (ticket: TrayTicket): TicketState => {
    const close = closes.get(ticket.autotask_ticket_id);
    if (close === undefined) {
      return ticket.state;
    }
    const action = actionOf(ticket.autotask_ticket_id);
    if (action === undefined || isUnfinished(action)) {
      return "closing";
    }
    return action.state === "done" ? "closed" : "close_failed";
  };
  // What the tray shows: the API's rows, and those just closed that it no
  // longer gives, oldest first.
  const listed = new Set((rows ?? []).map((t) => t.autotask_ticket_id));
  const shown = [
    ...(rows ?? []),
    ...[...closes.entries()]
      .filter(([id, close]) => !listed.has(id) && close.row !== undefined && actionOf(id)?.state === "done")
      .flatMap(([, close]) => (close.row === undefined ? [] : [close.row])),
  ].sort(byAge);
  const closable = shown.filter((t) => canClose(stateOf(t))).map((t) => t.autotask_ticket_id);
  const picked = [...checked].filter((id) => closable.includes(id));
  const allPicked = closable.length > 0 && closable.every((id) => checked.has(id));
  // Every one not closed, as the tile counts them.
  const open = (rows ?? []).filter((t) => t.state !== "closed");
  const oldest = oldestOf(open);
  const total = open.reduce((sum, t) => sum + t.logged_seconds, 0);
  const whose = otherName === null ? "your" : `${otherName}'s`;
  const today = todayIn(timeZone, now);

  const goTo = (ticket: TrayTicket) => {
    const name = nameOf(ticket);
    if (ticket.secondary_only) {
      // Its time is its primary's, on their days: none of it on this tech's.
      setNotice({
        text: `${otherName === null ? "You're" : `${otherName} is`} a secondary resource on ${name}${
          ticket.primary_name === null ? "" : `, ${ticket.primary_name}'s ticket`
        }. Drop a Ready block on it to log time there.`,
        day: null,
      });
      return;
    }
    const onBoard = blocks.filter((b) => b.ticket?.ticketId === ticket.autotask_ticket_id);
    const last = onBoard.at(-1);
    if (last !== undefined) {
      setNotice(null);
      onOpenBlock(last.id);
      return;
    }
    if (entries.some((e) => e.ticket?.ticketId === ticket.autotask_ticket_id)) {
      setNotice({ text: `${name}'s time was logged straight in Autotask: it's the dashed card on the board, not a block.`, day: null });
      return;
    }
    if (ticket.last_logged_at === null) {
      setNotice({
        text: `${name} was created in Autotask and nothing's logged to it yet, so there's no block to open. Drop a Ready block on it to log time there.`,
        day: null,
      });
      return;
    }
    const lastDay = todayIn(timeZone, new Date(ticket.last_logged_at));
    setNotice({
      text: `${name} has no block on this day: it was last logged ${formatShortDay(lastDay)}.`,
      day: lastDay === day ? null : lastDay,
    });
  };

  return (
    // Flush with the window's right edge and foot (ADR 0190): square there,
    // its border and shadow on the left.
    <aside
      id={id}
      aria-labelledby={headingId}
      aria-hidden={leaving || undefined}
      inert={leaving}
      data-part="open-tickets-tray"
      className={
        "flex h-full w-full flex-col border-t border-l border-line bg-panel " +
        "shadow-[-18px_0_40px_rgba(0,0,0,0.12)] min-[560px]:rounded-tl-panel"
      }
    >
      {/* On a phone the tab rides inside the drawer's left edge: room for it. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-5 pt-[18px] pb-3 max-[559px]:pr-4 max-[559px]:pl-10">
        <div className="flex items-center gap-2.5">
          <h2
            id={headingId}
            ref={heading}
            tabIndex={-1}
            className="text-[12.5px] font-[650] tracking-[0.08em] whitespace-nowrap text-muted uppercase"
          >
            Open tickets
          </h2>
          <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-pill border border-line px-1.5 font-mono text-[11.5px]">
            {tray.data === undefined ? "—" : open.length}
          </span>
          <span className="min-w-0 truncate text-[13px] text-muted">
            {tray.data === undefined
              ? tray.isError
                ? "Couldn't load them"
                : "Loading…"
              : oldest === null
                ? "None open"
                : `oldest ${ageOf(oldest, now, timeZone)} · ${formatDuration(total)} logged`}
          </span>
          <IconButton aria-label="Close the tray" title="Close (Esc)" onClick={onClose} className="ml-auto shrink-0 text-lg leading-none">
            <span aria-hidden="true">×</span>
          </IconButton>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <label className="flex items-center gap-2 text-[13px] text-muted">
            <input
              type="checkbox"
              checked={allPicked}
              disabled={closable.length === 0}
              onChange={(event) => {
                setChecked(new Set(event.target.checked ? closable : []));
                setBulkAsking(false);
              }}
              className="size-4 accent-brand"
            />
            Select all
          </label>
          <button
            type="button"
            disabled={picked.length === 0 || closeMany.isPending}
            onClick={() => {
              setBulkAsking(true);
            }}
            className="ml-auto h-9 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {picked.length > 0 ? `Close selected (${String(picked.length)})` : "Close selected"}
          </button>
        </div>
        {bulkAsking && picked.length > 0 ? (
          <div role="group" aria-label="Close selected" className="flex flex-col gap-2 rounded-row border border-line bg-panel-2 px-3 py-2.5">
            <p className="text-[13.5px] font-semibold">{`Close ${count(picked.length, "ticket", "tickets")}?`}</p>
            <ul aria-label="To close" className="flex flex-wrap gap-1.5">
              {shown
                .filter((t) => picked.includes(t.autotask_ticket_id))
                .map((t) => (
                  <li key={t.autotask_ticket_id} className="rounded-pill border border-line bg-panel px-2 py-0.5 font-mono text-[12px]">
                    {nameOf(t)}
                  </li>
                ))}
            </ul>
            <p className="text-[12.5px] text-muted">
              Each is set to <b>Complete</b> in Autotask, written as the tech who logged it. One that&apos;s already
              Complete is left as it is.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  closeMany.mutate({ ids: picked, key: crypto.randomUUID() });
                  setChecked(new Set());
                  setBulkAsking(false);
                }}
                className="h-9 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13px] font-bold text-cta-fg"
              >
                {`Close ${count(picked.length, "ticket", "tickets")}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setBulkAsking(false);
                }}
                className="h-9 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13px] font-semibold hover:bg-panel-2"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
        {notice === null ? null : (
          <p role="status" className="rounded-row border border-dashed border-line px-3 py-2 text-[12.5px] text-muted">
            {notice.text}{" "}
            {notice.day === null ? null : <DayLink day={notice.day}>{`Open ${formatShortDay(notice.day)}`}</DayLink>}
          </p>
        )}
        {/* One scroll for both lists: the tech's own, then the lower section. */}
        <div className="-mx-0.5 flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-0.5 pt-0.5 pb-2">
          {tray.data !== undefined && shown.length === 0 ? (
            <p className="text-[13px] text-muted">No open tickets. Everything you&apos;ve logged is closed.</p>
          ) : (
            <ul aria-label="Open tickets" className="flex flex-col gap-2.5">
              {shown.map((ticket) => {
                const id = ticket.autotask_ticket_id;
                const state = stateOf(ticket);
                return (
                  <TrayRow
                    key={id}
                    ticket={ticket}
                    state={state}
                    now={now}
                    today={today}
                    timeZone={timeZone}
                    otherName={otherName}
                    checked={checked.has(id)}
                    onCheck={(on) => {
                      setChecked((last) => {
                        const next = new Set(last);
                        if (on) {
                          next.add(id);
                        } else {
                          next.delete(id);
                        }
                        return next;
                      });
                      setBulkAsking(false);
                    }}
                    asking={asking === id}
                    onAsk={(on) => {
                      setAsking(on ? id : null);
                    }}
                    onConfirm={() => {
                      setAsking(null);
                      closeOne.mutate({ id, key: crypto.randomUUID() });
                    }}
                    onTryAgain={() => {
                      // Its failed close, tried again: never a second close
                      // (ADR 0186). Only one with none known closes it anew.
                      const actionId = closes.get(id)?.actionId ?? ticket.close_action_id;
                      if (actionId === null) {
                        closeOne.mutate({ id, key: crypto.randomUUID() });
                      } else {
                        tryAgain.mutate({ id, actionId });
                      }
                    }}
                    refusal={refused.get(id) ?? null}
                    onNumber={() => {
                      goTo(ticket);
                    }}
                    drop={dropOn(`tray:${String(id)}`, {
                      ticketId: id,
                      number: ticket.ticket_number,
                      state,
                      clientId: ticket.client?.id ?? null,
                      clientName: ticket.client?.name ?? null,
                    })}
                  />
                );
              })}
            </ul>
          )}
          {secondary.length === 0 ? null : (
            <section aria-labelledby={secondaryId} className="mt-2 flex flex-col gap-2.5 border-t border-line pt-3">
              <h3 id={secondaryId} className="text-[12px] font-[650] tracking-[0.06em] text-muted uppercase">
                {`${otherName === null ? "Tickets you're" : `Tickets ${otherName} is`} secondary on (${String(secondary.length)})`}
              </h3>
              <ul aria-label={otherName === null ? "Tickets you're secondary on" : `Tickets ${otherName} is secondary on`} className="flex flex-col gap-2.5">
                {secondary.map((ticket) => {
                  const id = ticket.autotask_ticket_id;
                  return (
                    <TrayRow
                      key={id}
                      ticket={ticket}
                      state={ticket.state}
                      secondary
                      now={now}
                      today={today}
                      timeZone={timeZone}
                      otherName={otherName}
                      checked={false}
                      onCheck={() => undefined}
                      asking={false}
                      onAsk={() => undefined}
                      onConfirm={() => undefined}
                      onTryAgain={() => undefined}
                      refusal={null}
                      onNumber={() => {
                        goTo(ticket);
                      }}
                      drop={dropOn(`tray:${String(id)}`, {
                        ticketId: id,
                        number: ticket.ticket_number,
                        state: ticket.state,
                        clientId: ticket.client?.id ?? null,
                        clientName: ticket.client?.name ?? null,
                      })}
                    />
                  );
                })}
              </ul>
            </section>
          )}
        </div>
        <p className="text-[12.5px] text-muted">
          {`The board shows one day; this lists ${whose} open tickets from every day, including ones created in Autotask, oldest first. Drop a Ready block on one to log it there.`}
        </p>
      </div>
    </aside>
  );
}

/** A link to `day` on the day view, keeping who's shown and the tray open. */
function DayLink({ day, children }: { day: string; children: string }) {
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  params.set("date", day);
  params.delete("block");
  return (
    <Link to={{ search: `?${params.toString()}` }} className="font-semibold text-brand-ink hover:underline">
      {children}
    </Link>
  );
}

function TrayRow({
  ticket,
  state,
  secondary = false,
  now,
  today,
  timeZone,
  otherName,
  checked,
  onCheck,
  asking,
  onAsk,
  onConfirm,
  onTryAgain,
  refusal,
  onNumber,
  drop,
}: {
  ticket: TrayTicket;
  /** As the tray sees it now: closing as soon as it's asked for, closed while it leaves. */
  state: TicketState;
  /** One they're only a secondary resource on (ADR 0192): its primary, no checkbox, no Close. */
  secondary?: boolean;
  now: Date;
  today: string;
  timeZone: string;
  otherName: string | null;
  checked: boolean;
  onCheck: (on: boolean) => void;
  asking: boolean;
  onAsk: (on: boolean) => void;
  onConfirm: () => void;
  onTryAgain: () => void;
  refusal: string | null;
  onNumber: () => void;
  drop: DropZone;
}) {
  const name = nameOf(ticket);
  const chip = STATE_CHIPS[state];
  const { text, warn } = stateWords(ticket, state);
  const age = ticket.age_from === null ? null : ageOf(ticket.age_from, now, timeZone);
  // Made in Autotask: never logged to through Axon Time by this tech.
  const fromAutotask = !secondary && !ticket.reasons.includes("logged_axon_time");
  const why = fromAutotask ? whyWords(ticket.reasons, otherName) : [];
  const lastDay = ticket.last_logged_at === null ? null : todayIn(timeZone, new Date(ticket.last_logged_at));
  return (
    <li
      data-ticket={name}
      className={
        "relative flex min-w-0 gap-3 rounded-card border border-line bg-card px-3.5 py-3" +
        (state === "closing" ? " opacity-85" : state === "closed" ? " opacity-70" : "")
      }
      onDragEnter={drop.onDragEnter}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      {secondary ? null : (
        <input
          type="checkbox"
          aria-label={`Select ${name}`}
          checked={checked && canClose(state)}
          disabled={!canClose(state)}
          onChange={(event) => {
            onCheck(event.target.checked);
          }}
          className="mt-0.5 size-4 shrink-0 accent-brand"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <button
            type="button"
            onClick={onNumber}
            className="cursor-pointer text-left text-[15px] font-[650] tracking-[-0.01em] hover:text-brand-ink hover:underline"
          >
            {name}
          </button>
          {age === null ? null : age === "today" ? (
            <span className="text-[13px] font-semibold whitespace-nowrap">today</span>
          ) : (
            <span className="text-[22px] leading-none font-semibold tracking-[-0.04em] whitespace-nowrap">
              {age.replace(" d", "")}
              <small className="ml-0.5 text-[12px] font-medium tracking-normal text-muted">d</small>
            </span>
          )}
        </div>
        <p className="text-[13px] [overflow-wrap:anywhere]">
          {ticket.client?.name ?? "No client"}
          {ticket.client?.is_hipaa ? (
            <>
              {" "}
              <Badge tone="hipaa">HIPAA</Badge>
            </>
          ) : ticket.title ? (
            <span className="text-muted">{` · ${ticket.title}`}</span>
          ) : null}
        </p>
        {fromAutotask ? (
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-muted">
            <Badge tone="neutral">Created in Autotask</Badge>
            {why.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
        ) : null}
        {secondary ? (
          <p className="text-[12.5px] text-muted">
            {ticket.primary_name === null ? "No primary resource" : `Primary: ${ticket.primary_name}`}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-muted">
          <Badge tone={chip.tone}>{chip.label}</Badge>
          {text === null ? null : (
            <span className={warn ? "font-semibold text-status-needs-client-fg" : undefined}>{text}</span>
          )}
          <span>
            {`· ${ticket.logged_seconds > 0 ? `${formatDuration(ticket.logged_seconds)} logged` : "No time logged yet"}`}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[12.5px] text-muted">
            {lastDay === null ? (
              ticket.age_from === null ? null : (
                `Created ${formatShortDay(todayIn(timeZone, new Date(ticket.age_from)))}`
              )
            ) : lastDay === today ? (
              "Last logged today"
            ) : secondary ? (
              // By its primary, on their day: no link to this tech's.
              `Last logged ${formatShortDay(lastDay)}`
            ) : (
              <>
                {"Last logged "}
                <DayLink day={lastDay}>{formatShortDay(lastDay)}</DayLink>
              </>
            )}
          </span>
          {secondary ? null : asking && state === "open" ? (
            <span role="group" aria-label={`Close ticket ${name}`} className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12.5px]">
              <span>
                Set it to <b>Complete</b> in Autotask?
              </span>
              <button
                type="button"
                onClick={onConfirm}
                className="h-8 cursor-pointer rounded-pill bg-cta-bg px-3.5 text-[12.5px] font-bold text-cta-fg"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  onAsk(false);
                }}
                className="h-8 cursor-pointer rounded-pill border border-line bg-panel px-3.5 text-[12.5px] font-semibold hover:bg-panel-2"
              >
                Cancel
              </button>
            </span>
          ) : state === "open" ? (
            <button
              type="button"
              aria-label={`Close ticket ${name}`}
              onClick={() => {
                onAsk(true);
              }}
              className="h-8 cursor-pointer rounded-pill border border-line bg-panel px-3.5 text-[12.5px] font-semibold hover:bg-panel-2"
            >
              Close
            </button>
          ) : state === "close_failed" ? (
            <button
              type="button"
              aria-label={`Try again to close ticket ${name}`}
              onClick={onTryAgain}
              className="h-8 cursor-pointer rounded-pill border border-line bg-panel px-3.5 text-[12.5px] font-semibold hover:bg-panel-2"
            >
              Try again
            </button>
          ) : null}
        </div>
        {refusal === null ? null : (
          <p role="alert" className="text-[12.5px] text-status-needs-client-fg">
            {`Couldn't close it: ${refusal}`}
          </p>
        )}
      </div>
      {drop.hint ? <DropNote hint={drop.hint} /> : null}
    </li>
  );
}
